import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import { useEffect, useState } from "react";
import { routes } from "../../router/mobile/routes";
import {
  setLayoutState,
  setMenuOpen,
  setTopRightIcon,
} from "../../redux/Layout/layout.slice";
import { IAnalyticsController } from "../../hexagon/adapters/driver/analyticsController";
import { useCurrentPath } from "../../hooks/useCurrentPath";
import { setLocalBetaFeature } from "../../redux/Setting/setting.slice";
import {
  createSearchParams,
  Location,
  useSearchParams,
} from "react-router-dom";
import {
  setRedirectToUserInfo,
  setUserClinic,
} from "../../redux/Clinic/clinic.slice";
import { setDashboardStateReset } from "../../redux/Dashboard/dashboard.slice";
import { ISummaryController } from "../../hexagon/adapters/driver/summaryController";
import { setSummaryState } from "../../redux/Summary/summary.slice";
import { IlocalStorageProvider } from "../../hexagon/adapters/driven/providers/localStorageProvider";
import { useVitalsAIMode } from "../../hooks/useVitalsAIMode";

const tapEventTabs = [
  {
    key: routes.dashboard.path,
    tapEvent: "Tap on PSN dashboard",
  },
  {
    key: routes.clinical.path,
    tapEvent: "Tap on Clinical alerts",
  },
  {
    key: routes.watchlist.path,
    tapEvent: "Tap on watchlist",
  },
  {
    key: routes.reports.path,
    tapEvent: "Tap on reports",
  },

  {
    key: routes.myFacility.path,
    tapEvent: "Tap on my facility",
  },
];

export interface IUseHook {
  analyticsController: IAnalyticsController;
  location: Location;
  clinicController: IClinicController;
  populateAppData: (changeFacility?: boolean) => Promise<void>;
  summaryController: ISummaryController;
  localStorageProvider: IlocalStorageProvider;
}

export const useHook = ({
  analyticsController,
  location,
  clinicController,
  populateAppData,
  summaryController,
  localStorageProvider,
}: IUseHook) => {
  const dispatch = useAppDispatch();
  const pathname = useCurrentPath();
  const navigate = useAppNavigate();
  const [confirmLogout, setConfirmLogout] = useState<boolean>(false);

  const onLocalBetaFeature = (val: boolean) => {
    dispatch(setLocalBetaFeature(val));
  };

  const {
    user,
    userClinic,
    topRightIcon,
    showHeader,
    localBetaFeature,
    menuOpen,
    connectionStatus,
    clinicList,
    selectedFloors,
    redirectToUserInfo,
  } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
    clinicList: state.clinic.clinicList,
    topRightIcon: state.layout.topRightIcon,
    showHeader: state.layout.showHeader,
    localBetaFeature: state.settings.localBetaFeature,
    menuOpen: state.layout.menuOpen,
    connectionStatus: state.network.connectionStatus,
    selectedFloors: state.clinic.selectedFloors,
    redirectToUserInfo: state.clinic.redirectToUserInfo,
  }));

  // const email = user?.email || "";
  // const name = user?.fullName || "";

  useEffect(() => {
    if (redirectToUserInfo && localStorageProvider.get("sso") === "true") {
      navigate(routes.userInfo.path);
      dispatch(setRedirectToUserInfo(false));
    }
  }, [redirectToUserInfo]);

  // commented delighted survey as moved to custom component <AppPerformanceSurvey />
  // const properties = {
  //   facility: userClinic?.name || "",
  // };
  // if (email && !email.includes("circadia")) {
  // if (!isMobile()) {
  //   window.delighted.survey({
  //     email,
  //     name,
  //     properties,
  //   });
  // }

  const onRightIconClick = () => {
    if (location.pathname === "/watchlist" && topRightIcon?.type === "custom") {
      analyticsController.logEvent("Tap on add icon button in watchlist", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    if (topRightIcon && topRightIcon.type === "custom") {
      topRightIcon.action?.();
      return;
    }
    if (location.pathname === "/dashboard" && topRightIcon?.type === "filter") {
      analyticsController.logEvent("Tap on filter button in dashboard", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }

    if (
      location.pathname === "/clinical" &&
      topRightIcon?.type === "calender"
    ) {
      analyticsController.logEvent("Tap on Date button in clinic", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    if (location.pathname === "/reports" && topRightIcon?.type === "calender") {
      analyticsController.logEvent("Tap on Date button in reports", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }

    if (topRightIcon) {
      dispatch(setTopRightIcon({ ...topRightIcon, open: true }));
    }
  };

  const toggleMenu = (e?: { stopPropagation: () => void }) => {
    if (e) e.stopPropagation();
    if (!menuOpen) {
      analyticsController.logEvent("Tap on setting menu", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    } else {
      analyticsController.logEvent("Tap on close setting menu", {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
    dispatch(setMenuOpen(!menuOpen));
  };

  const onClickSearch = () => {
    if (location.pathname === "/devices") {
      dispatch(
        setTopRightIcon({ type: "search", open: false, openSearch: true })
      );
      return;
    }
    analyticsController.logEvent("Global search", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
    });
    navigate(routes.search.path);
  };

  const showHeaderBar = () => {
    if (!showHeader) return false;
    const menuRoutes: TMobileRouterPath[] = [
      routes.clinical.path,
      routes.reports.path,
      routes.myFacility.path,
      routes.rtBilling.path,
      routes.dashboard.path,
      routes.watchlist.path,
      routes.bedexits.path,
      routes.motionAndExits.path,
      routes.Insights.path,
      routes.patient.path,
      routes.evaluationAndManagement.path,
      routes.deviceOrders.path,
      routes.neo.path,
      routes.intercom.path,
      routes.billingModule.path,
      // routes.vitalSync.path, // Removed to show menu icon inside VitalSync page header
    ];
    // First, handle exact matches against known menu routes.
    if (menuRoutes.includes(location.pathname as TMobileRouterPath)) {
      return true;
    }
    // Fallback: if URLPattern is available, try to match parameterized route
    // templates (e.g. /patient/:patientId/:tab?) against the current path.
    if (typeof window !== "undefined" && "URLPattern" in window) {
      const currentPath = location.pathname as string;
      const origin = window.location.origin;
      const matchesPattern = menuRoutes.some((menuRoute) => {
        try {
          const pattern = new (window as any).URLPattern(
            menuRoute as string,
            origin
          );
          return pattern.test(currentPath, origin);
        } catch {
          // If a route template is not a valid URLPattern, skip it.
          return false;
        }
      });
      if (matchesPattern) {
        return true;
      }
    }
    return false;
  };

  const hideHeaderBorder = () => {
    const menuRoutes: TMobileRouterPath[] = [
      routes.clinical.path,
      routes.reports.path,
      routes.myFacility.path,
    ];
    return menuRoutes.includes(location.pathname as TMobileRouterPath);
  };

  const isVitalsAIMode = useVitalsAIMode();

  const showFooter = () => {
    // Hide footer in vitals AI mode since users will always be redirected to vitals-ai
    if (isVitalsAIMode) {
      return false;
    }

    const menuRoutes: TMobileRouterPath[] = [
      routes.clinical.path,
      routes.watchlist.path,
      routes.reports.path,
      routes.myFacility.path,
      routes.rtBilling.path,
      routes.dashboard.path,
      routes.patientProfile.path,
      routes.patientProfileFacility.path,
      routes.patientProfileForRedirect.path,
      routes.alertDetails.path,
      routes.dailyReport.path,
      routes.bedexits.path,
      routes.motionAndExits.path,
      routes.bedExitPatientChart.path,
      routes.Insights.path,
      routes.vitalSync.path,
      routes.evaluationAndManagement.path,
      routes.deviceOrders.path,
      routes.neo.path,
      routes.billingModule.path,
      routes.intercom.path,
      // Pages reachable from the bottom bar (pinnable tabs / "More" sheet)
      // that render no back affordance of their own. Without the footer the
      // user would be stranded there on mobile, since the header (and its
      // menu icon) is also hidden outside showHeaderBar()'s allow-list.
      routes.overview.path,
      routes.iq.path,
      routes.medRecAI.path,
      routes.billing.path,
      routes.productUtilizationDashboard.path,
      routes.npProductivityTimeline.path,
      // Deep-link-only page with no back button; keep the footer so a shared
      // link doesn't strand the recipient.
      routes.trends.path,
    ];
    return menuRoutes.includes(pathname as TMobileRouterPath);
  };

  const onCancel = () => {
    setConfirmLogout(false);
  };

  const onLogoutClick = () => {
    setConfirmLogout(true);
  };

  const footerTapEvent = (route: string) => {
    const tab = tapEventTabs.find((item) => item.key === route);
    if (tab) {
      analyticsController.logEvent(tab.tapEvent, {
        email: user?.email,
        selectedFacility: userClinic?.name,
        selectedFacilityId: userClinic?.id,
        cUserId: user?.userID,
      });
    }
  };

  // Change facility before loading page
  const [showLoading, setShowLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const clinicId = searchParams.get("clinic_id");

  const { notificationSettings } = useAppSelector((state) => ({
    notificationSettings: state.settings.notifications,
  }));

  const changeFacility = async (clinicId: number) => {
    const selectedClinic = await clinicController.setSelectedClinic({
      id: clinicId,
      clinicList,
      notificationSettings: {
        email: notificationSettings?.email || false,
        pushNotification: notificationSettings?.pushNotification || false,
        kioskMode: notificationSettings?.kioskMode || false,
        sms: notificationSettings?.sms || false,
        escalatedAlertPreference:
          notificationSettings?.escalatedAlertPreference || [],
        psnAlertPreference: notificationSettings?.psnAlertPreference || [],
        bedExitPreference: notificationSettings?.bedExitPreference || [],
        facilitiesToSendNotificationForOldId: [clinicId],
        smsPreferences: notificationSettings?.smsPreferences || {
          morning_report: false,
          afternoon_report: false,
          evening_report: false,
          just_in_time: false,
        },
      },
    });
    if (selectedClinic) {
      dispatch(setUserClinic({ clinic: selectedClinic }));
      dispatch(setLayoutState({ pageTabs: {} }));
      dispatch(setDashboardStateReset());
      await populateAppData(true);
    }
  };

  useEffect(() => {
    const changeFacilityAsync = async () => {
      setShowLoading(true);
      await changeFacility(Number(clinicId));
      setShowLoading(false);
    };

    if (clinicId && Number(clinicId) !== userClinic?.id) {
      changeFacilityAsync();
    }
  }, [clinicId]);
  //

  //summary
  const refreshSummary = async () => {
    if (selectedFloors && selectedFloors.length > 0) {
      const { success: summarySuccess, data: summaryData } =
        await summaryController.getSummary({
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          clinicId: userClinic?.id!,
          floorIds: selectedFloors,
          timezone: userClinic?.timezone || "",
        });
      if (summarySuccess && summaryData) {
        dispatch(setSummaryState({ summary: summaryData }));
      }
    }
  };
  //

  const onPatientClick = (patientId: number) => {
    navigate({
      pathname: routes.patientProfile.path.replace(
        ":patientId",
        patientId.toString()
      ),
    });
  };
  const onSetupClick = (patientId: number) => {
    navigate({
      pathname: routes.editBedExitAlertForSettings.path,
      search: createSearchParams({
        patientId: patientId.toString(),
      }).toString(),
    });
  };

  return {
    menuOpen,
    user,
    userClinic,
    topRightIcon,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    hideHeaderBorder,
    showFooter,
    onRightIconClick,
    uniquePageKey: location.key,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    pathname,
    connectionStatus,
    showLoading,
    refreshSummary,
    onPatientClick,
    onSetupClick,
  };
};
